import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getFirstTabbableElement } from '@/shared/helpers';
import { useStateWithRef } from '@/shared/hooks/use-state-with-ref';

export type WizardStepDefinition = {
  id: string;
  label: string;
  required?: boolean;
};

export type WizardStepSubmitHandler = () => boolean | Promise<boolean>;

type WizardMode = 'guided' | 'freeform';

type WizardStepChangeSource = 'programmatic' | 'user';

type WizardContextValue = {
  steps: WizardStepDefinition[];
  currentStepId: string;
  mode: WizardMode;
  isCurrentStepLast: boolean;
  areRequiredStepsComplete: boolean;
  isStepComplete: (stepId: string) => boolean;
  setCurrentStep: (stepId: string, source?: WizardStepChangeSource) => void;
  focusStep: (stepId: string, source?: WizardStepChangeSource) => void;
  setStepComplete: (stepId: string, complete: boolean) => void;
  registerStepElement: (stepId: string, element: HTMLElement | null) => void;
  registerStepSubmitHandler: (stepId: string, handler?: WizardStepSubmitHandler) => void;
  attemptCurrentStep: () => Promise<boolean>;
  attemptAllSteps: () => Promise<boolean>;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export function WizardProvider({
  steps,
  children,
}: PropsWithChildren<{
  steps: WizardStepDefinition[];
}>) {
  const [currentStepId, setCurrentStepId, currentStepIdRef] = useStateWithRef(
    () => steps[0]?.id ?? '',
  );
  const [mode, setMode] = useState<WizardMode>(() =>
    steps.some((step) => step.required) ? 'guided' : 'freeform',
  );
  const [stepCompletion, setStepCompletion] = useState<Record<string, boolean>>({});
  const stepElements = useRef<Record<string, HTMLElement | null>>({});
  const stepSubmitHandlers = useRef<Record<string, WizardStepSubmitHandler | undefined>>({});

  const isStepComplete = useCallback(
    (stepId: string) => Boolean(stepCompletion[stepId]),
    [stepCompletion],
  );

  const setStepComplete = useCallback(
    (stepId: string, complete: boolean) => {
      setStepCompletion((current) => {
        if (current[stepId] === complete) return current;

        const next = {
          ...current,
          [stepId]: complete,
        };

        if (steps.every((step) => !step.required || next[step.id])) {
          setMode('freeform');
        }

        return next;
      });
    },
    [steps],
  );

  const registerStepElement = useCallback((stepId: string, element: HTMLElement | null) => {
    if (element) {
      stepElements.current[stepId] = element;
    } else {
      delete stepElements.current[stepId];
    }
  }, []);

  const registerStepSubmitHandler = useCallback(
    (stepId: string, handler?: WizardStepSubmitHandler) => {
      if (handler) {
        stepSubmitHandlers.current[stepId] = handler;
      } else {
        delete stepSubmitHandlers.current[stepId];
      }
    },
    [],
  );

  const currentStepIndex = Math.max(
    steps.findIndex((step) => step.id === currentStepId),
    0,
  );
  const isCurrentStepLast = currentStepIndex === steps.length - 1;
  const areRequiredStepsComplete = steps.every((step) => !step.required || isStepComplete(step.id));

  const setCurrentStep = useCallback(
    (stepId: string, source: WizardStepChangeSource = 'programmatic') => {
      const previousStepId = currentStepIdRef.current;

      if (source === 'user' && previousStepId !== stepId && mode === 'guided') {
        const previousStepIndex = Math.max(
          steps.findIndex((step) => step.id === previousStepId),
          0,
        );
        const stepIndex = steps.findIndex((step) => step.id === stepId);
        const isAdvancingToNextStep = stepIndex === previousStepIndex + 1;

        if (
          stepIndex > previousStepIndex &&
          !(isAdvancingToNextStep && isStepComplete(previousStepId))
        ) {
          setMode('freeform');
        }
      }

      setCurrentStepId(stepId);
    },
    [currentStepIdRef, isStepComplete, mode, setCurrentStepId, steps],
  );

  const focusStep = useCallback(
    (stepId: string, source: WizardStepChangeSource = 'programmatic') => {
      setCurrentStep(stepId, source);

      const element = stepElements.current[stepId];
      if (!element) return;

      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      const focusTarget = getFirstTabbableElement(element) ?? element;
      focusTarget.focus({ preventScroll: true });
    },
    [setCurrentStep],
  );

  const attemptCurrentStep = useCallback(async () => {
    const submitHandler = stepSubmitHandlers.current[currentStepId];
    const valid = submitHandler ? await submitHandler() : true;

    if (!valid) {
      focusStep(currentStepId);
      return false;
    }

    const currentStepIndex = Math.max(
      steps.findIndex((step) => step.id === currentStepId),
      0,
    );
    const nextStepId =
      steps.find((step) => step.id !== currentStepId && step.required && !isStepComplete(step.id))
        ?.id ?? steps[currentStepIndex + 1]?.id;

    if (nextStepId) {
      focusStep(nextStepId);
    }

    return true;
  }, [currentStepId, focusStep, isStepComplete, steps]);

  const attemptAllSteps = useCallback(async () => {
    let firstInvalidStepId: string | undefined;

    for (const step of steps) {
      const submitHandler = stepSubmitHandlers.current[step.id];
      const valid = submitHandler
        ? await Promise.resolve(submitHandler()).catch(() => false)
        : true;

      if (!valid && !firstInvalidStepId) {
        firstInvalidStepId = step.id;
      }
    }

    if (firstInvalidStepId) {
      focusStep(firstInvalidStepId);
      return false;
    }

    return true;
  }, [focusStep, steps]);

  const value = useMemo(
    () => ({
      steps,
      currentStepId,
      mode,
      isCurrentStepLast,
      areRequiredStepsComplete,
      isStepComplete,
      setCurrentStep,
      focusStep,
      setStepComplete,
      registerStepElement,
      registerStepSubmitHandler,
      attemptCurrentStep,
      attemptAllSteps,
    }),
    [
      steps,
      currentStepId,
      mode,
      isCurrentStepLast,
      areRequiredStepsComplete,
      isStepComplete,
      setCurrentStep,
      focusStep,
      setStepComplete,
      registerStepElement,
      registerStepSubmitHandler,
      attemptCurrentStep,
      attemptAllSteps,
    ],
  );

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
}

export function useWizardContext() {
  const context = useContext(WizardContext);

  if (!context) {
    throw new Error('Wizard components must be used within WizardRoot');
  }

  return context;
}
